import { productPhotoMetadata } from '@iheartjane/menu';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import { Helmet } from 'react-helmet-async';

/* eslint-disable-next-line no-restricted-imports */
import type { MenuProduct, Product, Store } from '@jane/shared/models';
import { useEcommApp } from '@jane/shared/providers';
import {
  appendDispensaryToStoreName,
  statePostalCode,
} from '@jane/shared/util';

import { getCanonicalPath } from './getCanonicalPath';
import { OpenGraph } from './openGraph';

export const ProductDetailMetaData = ({
  path,
  product,
  store,
}: {
  path: string;
  product: Product | MenuProduct;
  store?: Store;
}) => {
  const { isIframe } = useEcommApp();
  const { brand_subtype, kind, brand, name: productName } = product;
  const { city, state, name: storeName } = store || {};
  const {
    isBoostForAll,
    isFramelessEmbedMode,
    isPartnerHosted,
    customProductDetailTitleSuffix,
    disableJaneTitleAndDescriptionMeta,
  } = useRuntimeConfig();

  const storeLocation =
    city && state ? `${city}, ${statePostalCode(state)}` : undefined;

  const titlePrefix = `Shop ${productName}`;
  const titleSuffix = customProductDetailTitleSuffix
    ? customProductDetailTitleSuffix
    : storeName && city && state
      ? `${appendDispensaryToStoreName(storeName, ' Dispensary')} ${storeLocation}`
      : 'Jane';

  const title = `${titlePrefix} | ${titleSuffix}`;

  const descriptionPrefix = `Find information about the ${productName}${
    brand_subtype ? ' ' + brand_subtype : ''
  }${kind === 'flower' ? ' strain' : ''}${brand ? ' from ' + brand : ''}`;

  const descriptionSuffix =
    storeName && city && state
      ? `for sale at ${appendDispensaryToStoreName(storeName, ' dispensary')} in ${storeLocation}`
      : 'for sale on Jane';

  const description = `${descriptionPrefix} ${descriptionSuffix}`;

  const url = getCanonicalPath({
    inIframe: isIframe,
    isBoostWp: isFramelessEmbedMode && !isBoostForAll,
    pagePath: path,
  });

  const image = productPhotoMetadata(product).url;

  const disableProductMeta = Boolean(disableJaneTitleAndDescriptionMeta);

  // We remove preset WP canonicals that don't have the /products/ path because they are incorrect
  // Specific to frameless product pages
  if (isFramelessEmbedMode && !isBoostForAll) {
    const presetCanonical = document.querySelectorAll(
      'link:not([href*="/products/"])[rel="canonical"]'
    )[0];
    if (presetCanonical) {
      presetCanonical.remove();
    }
  }

  return (
    <>
      <Helmet>
        {!disableProductMeta && <title>{title}</title>}

        {/** Url & product description metadata is set in boost4all controller https://github.com/janetechinc/iheartjane/blob/b36d47cefe38578e391ecdd3d33addf12bd1f425/api/app/lib/boost/compilation/compile.rb#L35 */}
        {(!disableProductMeta || !isBoostForAll) && (
          <meta name="description" content={description} />
        )}
        {!isBoostForAll && <link rel="canonical" href={url} />}
      </Helmet>
      <OpenGraph
        description={description}
        title={title}
        url={url}
        image={image}
        type="product"
        isPartnerHosted={isPartnerHosted}
        isProductDetail
        isBoostForAll={isBoostForAll}
        disableMeta={disableProductMeta}
      />
    </>
  );
};
